import type { RacePhase } from '../shared/types.js';
import { SYSTEM_RACE_CLOCK, type RaceClock } from '../shared/race-clock.js';

export type RaceSessionMode = 'online' | 'offline' | 'spectator';
export type RaceRuntimePhase = 'idle' | 'mounting' | RacePhase;
export type RaceSessionOperation = 'none' | 'restarting' | 'transferring';

export interface RaceSessionState {
  sessionId: number;
  revision: number;
  roomId: string;
  mode: RaceSessionMode | null;
  phase: RaceRuntimePhase;
  attempt: number;
  operation: RaceSessionOperation;
  updatedAt: number;
}

export type RaceLifecycleEvent =
  | { type: 'begin'; roomId: string; mode: RaceSessionMode; attempt?: number }
  | { type: 'authoritative-phase'; phase: RacePhase; attempt: number }
  /** midRace: a solo time trial may restart while still racing. */
  | { type: 'restart-requested'; midRace?: boolean }
  | { type: 'restart-cancelled' }
  | { type: 'transfer-requested' }
  | { type: 'transfer-cancelled' }
  | { type: 'close' };

export interface TimedRaceLifecycleEvent {
  at: number;
  event: RaceLifecycleEvent;
}

export interface RaceLifecycleJournalEntry extends TimedRaceLifecycleEvent {
  index: number;
  before: RaceSessionState;
  after: RaceSessionState;
}

export class RaceLifecycleInvariantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RaceLifecycleInvariantError';
  }
}

export function initialRaceSessionState(at = 0): RaceSessionState {
  return {
    sessionId: 0,
    revision: 0,
    roomId: '',
    mode: null,
    phase: 'idle',
    attempt: 0,
    operation: 'none',
    updatedAt: at,
  };
}

export function reduceRaceSession(
  current: RaceSessionState,
  event: RaceLifecycleEvent,
  at: number,
): RaceSessionState {
  let next: RaceSessionState;
  switch (event.type) {
    case 'begin': {
      const roomId = event.roomId.trim();
      if (!roomId) throw new RaceLifecycleInvariantError('A race session requires a room id');
      const attempt = positiveAttempt(event.attempt ?? 1);
      next = {
        sessionId: current.sessionId + 1,
        revision: current.revision + 1,
        roomId,
        mode: event.mode,
        phase: 'mounting',
        attempt,
        operation: 'none',
        updatedAt: at,
      };
      break;
    }
    case 'authoritative-phase': {
      assertActive(current);
      const attempt = positiveAttempt(event.attempt);
      if (attempt < current.attempt) {
        throw new RaceLifecycleInvariantError(
          `Stale race attempt ${attempt} cannot replace attempt ${current.attempt}`,
        );
      }
      if (
        attempt === current.attempt
        && !authoritativePhaseAllowed(current.phase, event.phase)
      ) {
        throw new RaceLifecycleInvariantError(
          `Illegal authoritative phase transition ${current.phase} -> ${event.phase}`,
        );
      }
      next = {
        ...current,
        revision: current.revision + 1,
        phase: event.phase,
        attempt,
        operation: attempt > current.attempt ? 'none' : current.operation,
        updatedAt: at,
      };
      if (next.operation === 'restarting' && attempt > current.attempt) next.operation = 'none';
      break;
    }
    case 'restart-requested':
      assertActive(current);
      if (
        current.operation !== 'none'
        || (current.phase !== 'finished' && !(event.midRace && current.phase === 'racing'))
      ) {
        throw new RaceLifecycleInvariantError(
          'Restart is valid only for an idle finished session or a mid-race quick restart',
        );
      }
      next = updateOperation(current, 'restarting', at);
      break;
    case 'restart-cancelled':
      assertActive(current);
      next = updateOperation(current, current.operation === 'restarting' ? 'none' : current.operation, at);
      break;
    case 'transfer-requested':
      assertActive(current);
      if (current.mode !== 'online' || current.phase !== 'finished' || current.operation !== 'none') {
        throw new RaceLifecycleInvariantError('Party transfer is valid only for an idle finished online session');
      }
      next = updateOperation(current, 'transferring', at);
      break;
    case 'transfer-cancelled':
      assertActive(current);
      next = updateOperation(current, current.operation === 'transferring' ? 'none' : current.operation, at);
      break;
    case 'close':
      next = {
        ...initialRaceSessionState(at),
        sessionId: current.sessionId,
        revision: current.revision + 1,
      };
      break;
  }
  assertRaceSessionInvariant(next);
  return next;
}

export class RaceSessionLifecycle {
  private current: RaceSessionState;
  private readonly entries: RaceLifecycleJournalEntry[] = [];
  private eventIndex = 0;

  constructor(
    private readonly clock: RaceClock = SYSTEM_RACE_CLOCK,
    private readonly journalLimit = 128,
  ) {
    this.current = initialRaceSessionState(clock.wallNow());
  }

  get state(): Readonly<RaceSessionState> {
    return this.current;
  }

  get journal(): readonly RaceLifecycleJournalEntry[] {
    return this.entries;
  }

  dispatch(event: RaceLifecycleEvent, at = this.clock.wallNow()): Readonly<RaceSessionState> {
    const before = this.current;
    const after = reduceRaceSession(before, event, at);
    this.current = after;
    this.entries.push({ index: ++this.eventIndex, at, event, before, after });
    if (this.entries.length > Math.max(1, this.journalLimit)) this.entries.shift();
    return after;
  }

  begin(roomId: string, mode: RaceSessionMode, attempt = 1): Readonly<RaceSessionState> {
    return this.dispatch({ type: 'begin', roomId, mode, attempt });
  }

  observeAuthoritative(phase: RacePhase, attempt: number): Readonly<RaceSessionState> {
    if (this.current.phase === phase && this.current.attempt === attempt) return this.current;
    return this.dispatch({ type: 'authoritative-phase', phase, attempt });
  }

  requestRestart(midRace = false): Readonly<RaceSessionState> {
    return this.dispatch(midRace ? { type: 'restart-requested', midRace } : { type: 'restart-requested' });
  }

  cancelRestart(): Readonly<RaceSessionState> {
    if (this.current.operation !== 'restarting') return this.current;
    return this.dispatch({ type: 'restart-cancelled' });
  }

  requestTransfer(): Readonly<RaceSessionState> {
    return this.dispatch({ type: 'transfer-requested' });
  }

  cancelTransfer(): Readonly<RaceSessionState> {
    if (this.current.operation !== 'transferring') return this.current;
    return this.dispatch({ type: 'transfer-cancelled' });
  }

  close(): Readonly<RaceSessionState> {
    if (this.current.phase === 'idle') return this.current;
    return this.dispatch({ type: 'close' });
  }

  replay(): TimedRaceLifecycleEvent[] {
    return this.entries.map(({ at, event }) => ({ at, event }));
  }
}

export function replayRaceLifecycle(events: readonly TimedRaceLifecycleEvent[]): RaceSessionState {
  let state = initialRaceSessionState(events[0]?.at ?? 0);
  for (const entry of events) state = reduceRaceSession(state, entry.event, entry.at);
  return state;
}

export function assertRaceSessionInvariant(state: RaceSessionState): void {
  if (!Number.isInteger(state.sessionId) || state.sessionId < 0) {
    throw new RaceLifecycleInvariantError('Race session id must be a non-negative integer');
  }
  if (!Number.isInteger(state.revision) || state.revision < 0) {
    throw new RaceLifecycleInvariantError('Race session revision must be a non-negative integer');
  }
  if (state.phase === 'idle') {
    if (state.roomId || state.mode !== null || state.attempt !== 0 || state.operation !== 'none') {
      throw new RaceLifecycleInvariantError('An idle race session cannot retain runtime state');
    }
    return;
  }
  if (!state.roomId || state.mode === null || !Number.isInteger(state.attempt) || state.attempt < 1) {
    throw new RaceLifecycleInvariantError('An active race session requires room, mode, and attempt identity');
  }
  if (state.operation === 'restarting' && state.phase !== 'finished' && state.phase !== 'racing') {
    throw new RaceLifecycleInvariantError('A restart operation must belong to a racing or finished session');
  }
  if (state.operation === 'transferring' && (state.mode !== 'online' || state.phase !== 'finished')) {
    throw new RaceLifecycleInvariantError('A transfer operation must belong to a finished online session');
  }
}

function updateOperation(
  state: RaceSessionState,
  operation: RaceSessionOperation,
  at: number,
): RaceSessionState {
  return {
    ...state,
    operation,
    revision: state.revision + 1,
    updatedAt: at,
  };
}

function assertActive(state: RaceSessionState): void {
  if (state.phase === 'idle') throw new RaceLifecycleInvariantError('Race session is not active');
}

function positiveAttempt(value: number): number {
  if (!Number.isInteger(value) || value < 1) {
    throw new RaceLifecycleInvariantError('Race attempt must be a positive integer');
  }
  return value;
}

function authoritativePhaseAllowed(from: RaceRuntimePhase, to: RacePhase): boolean {
  if (from === 'mounting' || from === to) return true;
  if (from === 'lobby') return to === 'countdown';
  if (from === 'countdown') return to === 'racing';
  if (from === 'racing') return to === 'finished';
  return false;
}
